import { useTranslation } from '@fluxora/i18n';
import { ContactUsForm } from '../components/ContactUsForm';
import { useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';


import { env } from '../../../config/env';
import { FunnelStepper } from '../components/FunnelStepper';
import { ResumeRestartNotice } from '../components/ResumeRestartNotice';
import { ResumeWelcomeBanner } from '../components/ResumeWelcomeBanner';
// import { TrustStrip } from '../components/TrustStrip';

/**
 * The storefront app shell (Phase 1 storefront UI). Wraps every shop page as a
 * layout route: a FLOATING pill nav bar on top (theme2 "ERCOT Grid" language —
 * sticky, rounded, translucent, floating over the arctic canvas), a trust strip,
 * the routed page in the middle (`<Outlet />`), and a compliance-anchored footer.
 *
 * COMPLIANCE NOTES:
 *  - The PUCT REP licence number is a CLEARLY-MARKED PLACEHOLDER via an i18n key —
 *    nothing here invents a real regulatory value.
 *  - No star ratings / review counts anywhere (see TrustStrip).
 *
 * NAV (meter-first): the sole shop entry is the landing method chooser (Shop by
 * ESI ID / ZIP), so there is NO "Plans" tab and NO "Shop plans" header CTA — the
 * old plan-first `/plans` shelf is dead (see router; `/plans` redirects to the
 * landing chooser). The header keeps only the in-page anchors "How it works" /
 * "Support"; a small scroll-to-hash effect lands them from any route. The
 * placeholder support phone was removed with the CTA block.
 */

/** Inactive nav pill — mirrors theme2 `.crm-nav-h-item` (the anchors are never "active"). */
function navPillClass(): string {
  return 'rounded-full px-4 py-2 text-sm font-medium transition-colors text-foreground/70 hover:text-foreground hover:bg-primary/5 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2';
}

export function StorefrontLayout(): JSX.Element {
  const { t } = useTranslation();
  const { pathname, hash } = useLocation();
  const year = new Date().getFullYear();

  // Scroll-to-hash: landing-page anchors (`/#how-it-works`, `/#support`) must
  // resolve from any route. rAF lets the target page mount before we scroll.
  useEffect(() => {
    if (hash.length === 0) return;
    const id = hash.slice(1);
    const frame = requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
    return () => cancelAnimationFrame(frame);
  }, [pathname, hash]);



  return (
    <div className="flex min-h-full flex-col bg-bg">
      {/* Floating pill nav */}
      <header className="sticky top-0 z-40 px-4 pt-4">
        <nav
          aria-label={t('shop.trust.aria')}
          /* `backdrop-blur` (8px) on a STICKY bar re-blurs whatever scrolls
              under it on every frame — the single biggest scroll-jank source
              on this page. Near-opaque surface + 4px blur reads identically
              and costs a quarter of the paint. */
          className="mx-auto flex max-w-6xl items-center justify-between gap-4 rounded-full border border-border bg-surface/90 px-6 py-3 shadow-float backdrop-blur-sm"
        >
          <Link
            to="/"
            aria-label={t('shop.header.home.aria')}
            className="flex flex-col leading-tight rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
          >
            {/* The real lockup (mark + wordmark), not styled text. The SVG is
                the same file the customer portal ships, so the two surfaces can
                never drift apart on branding. Height-constrained; the SVG's own
                ratio (143:53) sets the width. The alt carries the brand name so
                nothing is lost to a screen reader. */}
            <img
              src="/brand/rheora-logo.svg"
              alt={t('shop.brand.name')}
              className="h-9 w-auto"
              draggable={false}
            />
          </Link>

          {/* In-page anchor nav (no "Plans" tab, no "Shop plans" CTA — meter-first;
              the sole entry is the landing method chooser). */}
          <div className="hidden items-center gap-1 md:flex">
            <Link to="/#how-it-works" className={navPillClass()}>
              {t('shop.nav.how')}
            </Link>
            <Link to="/#support" className={navPillClass()}>
              {t('shop.nav.support')}
            </Link>
          </div>
        </nav>
      </header>

      {/* <TrustStrip /> */}

      {/* Funnel progress — self-suppresses off the funnel (renders null), so the
          `empty:hidden` wrapper collapses to zero height on the shelf / status. */}
      <div className="mx-auto w-full max-w-6xl px-4 pt-6 empty:hidden">
        <FunnelStepper />
      </div>

      {/* E-CRM04 — "welcome back" on a session rebuilt from a resume link. */}
      <ResumeWelcomeBanner />
      <ResumeRestartNotice />

      {/* Routed page */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer
        id="support"
        className="mt-10 scroll-mt-24 rounded-t-lg border-t border-border bg-surface"
      >
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-start gap-10 px-4 py-10 text-sm text-foreground/70 sm:grid-cols-[1fr_1.3fr]">
          <div className="flex flex-col items-start gap-4">
            <div className="flex flex-col items-start gap-2">
              <img
                src="/brand/rheora-logo.svg"
                alt={t('shop.brand.name')}
                className="h-7 w-auto"
                draggable={false}
              />
              <span className="max-w-[36ch] text-xs leading-relaxed text-foreground/60">
                {t('shop.footer.secure')}
              </span>
            </div>

          {/*
            ⚠️ THESE ARE NOT LINKS, AND THAT IS THE FIX (E-SF19).

            The EFL, TOS and YRAC are the three regulatory documents a Texas
            customer is entitled to before they sign — and all three pointed at
            "/". Three dead links to three compliance documents reads as those
            documents being available when they are not.

            They are also genuinely PER-PLAN: §25.475 requires the EFL for the
            product being sold, so there is no single site-wide EFL to link to.
            The honest footer therefore names them and says where they live
            (each plan's own page, and again on the contract before signing)
            rather than manufacturing a destination.
          */}
          <div className="flex flex-col gap-2">
            <span className="text-xs font-semibold uppercase tracking-wide text-foreground/50">
              {t('shop.footer.docs.heading')}
            </span>
            <ul className="flex flex-col gap-1 text-foreground/70">
              <li>{t('shop.footer.docs.efl')}</li>
              <li>{t('shop.footer.docs.tos')}</li>
              <li>{t('shop.footer.docs.yrac')}</li>
            </ul>
            <span className="text-[11px] text-foreground/50">
              {t('shop.footer.docs.whereToFind')}
            </span>
          </div>

          {/*
            ⚠️ NO LICENCE NUMBER UNLESS A REAL ONE IS CONFIGURED (E-SF19).

            This block rendered the literal "PUCT REP License #XXXXX" under the
            caption "Placeholder — licence number pending configuration". A REP
            certificate number is a regulatory identifier a customer can check
            against the PUCT register; a fabricated one is not a placeholder, it
            is a false statement about our authorisation to sell electricity, and
            the caption does not repair it on a page a customer may screenshot.

            Absent is the honest state: a customer who sees no number knows to
            ask. Set VITE_PUCT_REP_LICENSE to the real certificate to restore it.
          */}
          {env.puctRepLicense === undefined ? null : (
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold uppercase tracking-wide text-foreground/50">
                {t('shop.footer.compliance.heading')}
              </span>
              <span className="font-medium text-foreground/80">
                {t('shop.footer.puctLicense', { number: env.puctRepLicense })}
              </span>
            </div>
          )}
          </div>

          {/* The enquiry column — the footer is where somebody looks when the
              page hasn't answered their question. Writes to the CRM's operator
              queue; see ContactUsForm. */}
          <ContactUsForm />
        </div>

        <div className="border-t border-border/60 px-4 py-4">
          <p className="mx-auto max-w-6xl text-center text-xs text-foreground/50">
            {t('shop.footer.copyright', { year })}
          </p>
        </div>
      </footer>
    </div>
  );
}
